import { useEffect, useRef } from 'react'
import { useSwipeNavBlock } from './useSwipeNavBlock'

/**
 * §7.1's Android-back mechanism: while a mode is open, one entry sits on the
 * browser's history stack, so the hardware back button leaves the mode instead
 * of the screen.
 *
 * Modes nest (editing mode, a card active inside it, search opened over it, a
 * selection made …) and one back press must resolve exactly one level. Two
 * properties of the History API make that impossible to get right one call
 * site at a time, which is why all the bookkeeping below is module-level and
 * shared rather than duplicated per site:
 *
 *  1. `history.state` holds exactly ONE object — whatever the topmost
 *     `pushState` wrote — not a merge of every live sentinel's marker. So
 *     "is my key in `history.state`?" can only ever answer "am I the top?",
 *     never "am I still on the stack". Sentinels are therefore identified by
 *     a monotonic id and tracked in the `desired` array below; `history.state`
 *     is written for debuggability, never read to make a decision.
 *
 *  2. `pushState` takes effect synchronously but `history.go()` does not — it
 *     queues a traversal that lands in a later task. A sentinel torn down in
 *     the same React commit as another is created would therefore queue a pop
 *     that lands AFTER the newcomer's push and removes the newcomer's entry.
 *     That is the ⋯-menu → panel handoff (`setShowMoreMenu(false)` and
 *     `setMobileFilterOpen(true)` in one click), and it is why panels opened
 *     from the menu were being torn down with it.
 *
 * Both are handled by never touching history from register/unregister at all.
 * Those only edit `desired` and schedule a reconcile, which runs once per
 * commit and diffs `desired` against what has actually been committed:
 * deeper → push the difference; shallower → one `go(-n)`; same depth with a
 * different sentinel on top → `replaceState`. The menu → panel handoff is that
 * last case: same depth, new top, no traversal, no race, still one level.
 *
 * There is ONE popstate listener for the whole app. Traversals this module
 * itself asked for are counted and swallowed centrally — the general form of
 * the `selfPopRef` guard the fullscreen card editor used to carry alone, which
 * could only ever swallow its own self-pop for its own listener and not for
 * anyone else's. Every other popstate is a real back press and closes exactly
 * the top of the stack, so nested sentinels cannot collapse together.
 */

type Sentinel = { id: number; onClose: () => void }

/** Live sentinels, bottom → top. The authority; `history.state` is not. */
let desired: Sentinel[] = []
/** Sentinel ids whose history entries have actually been pushed, bottom → top. */
let committed: number[] = []
/** Traversals this module requested whose `popstate` has not arrived yet. */
let expectedSelfPops = 0
let reconcileQueued = false
let listening = false
let nextId = 1

const STATE_KEY = '__sentinelId'

function onPopState() {
  // A traversal we asked for in `reconcile`. Not a user gesture — swallow it,
  // or it would read as a back press and close a mode nobody asked to leave.
  if (expectedSelfPops > 0) {
    expectedSelfPops--
    return
  }
  const top = desired[desired.length - 1]
  if (!top) return
  // A real back press. Exactly one level, whatever is nested below it.
  desired = desired.slice(0, -1)
  committed = committed.slice(0, -1)
  top.onClose()
  // `onClose` flips the caller's state, whose effect cleanup unregisters this
  // same sentinel on the next commit. `desired` has already dropped it, so
  // that unregister is a no-op and the reconcile it schedules sees no drift —
  // in particular it does not try to pop an entry the browser already popped.
  scheduleReconcile()
}

function scheduleReconcile() {
  if (reconcileQueued) return
  reconcileQueued = true
  // A microtask, so every register/unregister from one React commit is folded
  // into a single diff. Reconciling per call is what let a teardown and a
  // creation in the same commit race each other.
  queueMicrotask(reconcile)
}

function reconcile() {
  reconcileQueued = false
  const want = desired.length

  if (want > committed.length) {
    for (let i = committed.length; i < want; i++) {
      window.history.pushState({ [STATE_KEY]: desired[i].id }, '')
    }
    committed = desired.map((s) => s.id)
    return
  }

  if (want < committed.length) {
    const drop = committed.length - want
    // Only traverse if the top of the browser's stack is still ours. If
    // something navigated on top of us (a route change while a mode was
    // open), those entries are not ours to remove and going back would undo
    // the reader's navigation instead of closing a mode.
    const ours = committed.includes(window.history.state?.[STATE_KEY])
    committed = committed.slice(0, want)
    if (ours) {
      // One `go(-n)` call — however large `n` is — lands as ONE traversal and
      // fires exactly ONE `popstate`, not `n` of them. Crediting `drop` here
      // would overcount: after that single event decremented the counter once,
      // a leftover credit would sit swallowing the next REAL back press, one
      // that had nothing to do with this reconcile. This matters now that two
      // sentinels can close in the same commit (an operation's success clears
      // the selection and closes its own sheet together), which previously
      // never produced a `drop` greater than 1.
      expectedSelfPops += 1
      window.history.go(-drop)
    }
    return
  }

  // Same depth, different sentinel on top: the ⋯-menu → panel handoff. The
  // outgoing mode's entry is reused by the incoming one rather than being
  // popped and re-pushed, which is both cheaper and the only ordering that
  // cannot race.
  if (want > 0) {
    const topId = desired[want - 1].id
    if (committed[want - 1] !== topId) {
      committed = desired.map((s) => s.id)
      window.history.replaceState({ [STATE_KEY]: topId }, '')
    }
  }
}

/**
 * Keeps a history entry on the stack for as long as `active` is true, and
 * calls `onClose` when the hardware back button pops it.
 *
 * `onClose` is read through a ref, so an inline arrow at the call site does
 * not re-register the sentinel on every render — the entry is pushed once per
 * entry into the mode and popped once per exit, never once per change within
 * it. `label` never identifies a sentinel (see point 1 in the module comment
 * for why a name cannot); it names the mode for debugging, and is the reason
 * recorded for the swipe block below.
 *
 * A mode the back button closes is, by that fact, a mode that sits over the
 * screen, so every sentinel also joins the top-level swipe block (ADR-106 P3)
 * for exactly as long as it is active. That join is its own hook call, beside
 * the sentinel's effect rather than inside it, so it cannot change anything
 * about history or back handling.
 */
export function useHistorySentinel(active: boolean, label: string, onClose: () => void) {
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useSwipeNavBlock(active, `history-sentinel:${label}`)

  useEffect(() => {
    if (!active) return
    if (!listening) {
      listening = true
      window.addEventListener('popstate', onPopState)
    }
    const id = nextId++
    desired = [...desired, { id, onClose: () => onCloseRef.current() }]
    scheduleReconcile()
    return () => {
      // Already gone if `onPopState` closed this sentinel — then this is a
      // no-op and the reconcile below sees no drift.
      desired = desired.filter((s) => s.id !== id)
      scheduleReconcile()
    }
    // `label` is debug-only and `onClose` is behind a ref — neither should
    // re-arm the sentinel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])
}
